import { useState, useEffect } from 'react';
import { useDataPassingContext, DataPanelVisibleStates } from 'automation-ui-formula-editor/data-passing-panel/DataPassingContext';
import { DataPassingDispatchActionType } from 'automation-ui-formula-editor/data-passing-panel/constants/DataPassingActionConstants';
import DataPassingInputIndicator from 'automation-ui-formula-editor/data-passing-panel/DataPassingInputIndicator';
import { DataPanelModes } from 'automation-ui-formula-editor/data-passing-panel/types/DataPassingPanelTypes';
import useScrollDataPanelToDataToken from 'automation-ui-formula-editor/data-passing-panel/hooks/useScrollDataPanelToDataToken';
import { useActionConfigScrollContext } from 'automation-ui-flow-core/canvas/components/ActionConfigScrollColumns';
import { FLOW_HEADER_HEIGHT_FALLBACK } from 'automation-ui-core/constants/ui';
import { jsx as _jsx } from "react/jsx-runtime";
const useDataPassingPanel = () => {
  const [targetInputRef, setTargetInputRef] = useState();
  const {
    dispatch: dataPassingDispatch,
    isAddDataSourcePanelVisible,
    state: dataPassingState
  } = useDataPassingContext();
  const scrollToDataToken = useScrollDataPanelToDataToken();
  const {
    canvasScrollElementRef,
    dataPanelRef,
    isEmbedded,
    onChangeActionConfigDataPanelDisplay,
    searchForDataTokenCallback,
    targetInputRef: targetInputRefInState
  } = dataPassingState;

  // onMount open up the panel
  useEffect(() => {
    return function cleanup() {
      onChangeActionConfigDataPanelDisplay(DataPanelVisibleStates.CLOSED);
    };
    // DEV Note: We only want this to fire once so dep array should be blank
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const bindToDataPanel = ({
    dataPanelMode = DataPanelModes.PROPERTY_MODE,
    dataSourceAllowList,
    propertyFieldTypeAllowList,
    propertyTypeAllowList,
    hideNestedDataSources = false,
    onDataCoordinateSelected,
    onDataPropertySelectedCallback,
    refFromBindingComponent,
    requiredPermissionLevel
  }) => {
    dataPassingDispatch({
      type: DataPassingDispatchActionType.HYDRATE,
      payload: {
        dataPanelMode,
        dataSourceAllowList,
        propertyFieldTypeAllowList,
        propertyTypeAllowList,
        hideNestedDataSources,
        onDataCoordinateSelected,
        onDataPropertySelected: onDataPropertySelectedCallback,
        requiredPermissionLevel,
        targetInputRef: refFromBindingComponent
      }
    });
    setTargetInputRef(refFromBindingComponent);
    onChangeActionConfigDataPanelDisplay(DataPanelVisibleStates.OPEN);
  };
  const DataPassingVisualIndicator = ({
    heightOffset,
    heightOffsetAdjustment = 10
  }) => {
    const calculateHeightOffset = _heightOffsetAdjustment => {
      let height = 0;
      if (targetInputRef && targetInputRef.current && targetInputRef.current.getBoundingClientRect) {
        height = targetInputRef.current.getBoundingClientRect().top - _heightOffsetAdjustment;
      }
      if (!isEmbedded) {
        // add the height of the header to compensate for positional shift caused by the header height offset
        const {
          top: canvasTopOffset
        } = canvasScrollElementRef && canvasScrollElementRef.current && canvasScrollElementRef.current.getBoundingClientRect() || {
          top: FLOW_HEADER_HEIGHT_FALLBACK
        };
        height = height - canvasTopOffset;
      }
      return height;
    };
    const [calculatedHeightOffset, setCalculatedHeightOffset] = useState(calculateHeightOffset(heightOffsetAdjustment));
    const actionConfigScrollRef = useActionConfigScrollContext();
    useEffect(() => {
      const el = actionConfigScrollRef ? actionConfigScrollRef.current : null;
      const scrollEvent = () => {
        const newHeight = calculateHeightOffset(heightOffsetAdjustment);
        setCalculatedHeightOffset(newHeight);
      };
      if (el) {
        el.addEventListener('scroll', scrollEvent);
      }
      return () => {
        if (el) {
          el.removeEventListener('scroll', scrollEvent);
        }
      };
    }, [calculatedHeightOffset, actionConfigScrollRef, heightOffsetAdjustment]);
    return targetInputRefInState && targetInputRef && dataPanelRef && targetInputRefInState.current === targetInputRef.current ? /*#__PURE__*/_jsx(DataPassingInputIndicator, {
      dataPanelRef: dataPanelRef,
      heightOffset: heightOffset || calculatedHeightOffset,
      targetInputRef: targetInputRef
    }) : null;
  };
  return {
    bindToDataPanel,
    DataPassingVisualIndicator,
    isAddDataSourcePanelVisible,
    onChangeActionConfigDataPanelDisplay,
    scrollToDataToken,
    searchForDataTokenCallback
  };
};
export default useDataPassingPanel;